'use client';

import { RadioGroup, Select as SelectElement } from '@coolblue-development/freddy';
import changeSortingTrigger from '@coolblue-development/ga4-events/triggers/FilterAndSort/changeSortingTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { getSortingQueryParameterKey } from '@features/filter/utils';
import { useTranslation } from '@i18n/useTranslation';
import { useSearchParams } from 'next/navigation';
import { ReactNode, useContext, useState } from 'react';

import { FilterPageContext } from '../../context';
import { type Slug, SLUGS } from './slugs';

type SelectProps = {
  children: ReactNode;
  id: string;
  name: string;
  isMobile?: boolean;
};

const SortingWrapper = ({ children, id, name, isMobile }: SelectProps) => {
  const { trackEvent } = useAnalytics();
  const { updateSorting } = useContext(FilterPageContext);
  const { t } = useTranslation('webshop.filterPage.sortSelect');

  const { language } = useRequestContext();
  const sortingParameterKey = getSortingQueryParameterKey(language);
  const searchParams = useSearchParams();
  const defaultSortingValue = isMobile ? SLUGS['relevance'][language] : '';
  const [sortingValue, setSortingValue] = useState(
    searchParams?.get(sortingParameterKey) || defaultSortingValue
  );

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement> | string) => {
    const newValue = typeof event === 'string' ? event : event.target.value;

    setSortingValue(newValue);
    updateSorting?.(newValue as Slug);

    trackEvent(
      changeSortingTrigger({ sub_feature: 'filter_and_sort_overlay', sort_value: newValue })
    );
  };

  if (isMobile) {
    return (
      <RadioGroup name={t('mobileLabel')} value={sortingValue} onChange={handleChange}>
        {children}
      </RadioGroup>
    );
  }

  return (
    <SelectElement id={id} name={name} value={sortingValue} onChange={handleChange}>
      {children}
    </SelectElement>
  );
};

export default SortingWrapper;
